import { LockKey } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ReactNode } from 'react';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermission, useSession } from '@/features/auth/session';
import { sqk, subscriptionApi } from './api';

/** Recursos do plano da loja. Enquanto carrega (ou se a consulta falhar) não bloqueia: o servidor continua sendo a barreira. */
export function useFeature(key: string): { enabled: boolean; loading: boolean; planName: string | null } {
  const { membership } = useSession();
  const q = useQuery({ queryKey: sqk.features(membership?.organizationId), queryFn: subscriptionApi.features, staleTime: 60_000, retry: false });
  return { enabled: q.data ? q.data.features[key] !== false : true, loading: q.isPending, planName: q.data?.planName ?? null };
}

/** Tela de um recurso por plano: sem o recurso, mostra o convite claro de upgrade (e não uma tela que daria 403). */
export function FeatureGate({ feature, title, children }: { feature: string; title: string; children: ReactNode }) {
  const { enabled, loading, planName } = useFeature(feature);
  const canManage = usePermission('settings.manage');
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (enabled) return children;
  return (
    <EmptyState
      icon={LockKey}
      title={`${title} não está incluído no seu plano`}
      description={`${planName ? `O plano ${planName} não inclui` : 'Seu plano não inclui'} este recurso. Faça upgrade para o Profissional ou superior para liberar.`}
      action={
        canManage ? (
          <Button asChild>
            <Link to="/configuracoes/assinatura">Ver planos e fazer upgrade</Link>
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">Peça a quem administra a conta para fazer o upgrade.</p>
        )
      }
    />
  );
}
